/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useRef } from 'react';
import { Modal as RNModal, Platform, type ModalProps } from 'react-native';
import {
  dispatchModalEscape,
  modalEscapeStackSize,
  msSinceLastEscapeDispatch,
  pushModalEscape,
  removeModalEscape,
} from './modalEscapeStack';

/**
 * Drop-in de `Modal` de react-native.
 *
 * En web agrega cierre con Escape confiable: un `keydown` en `document` dispara
 * el `onRequestClose` del modal más alto del stack (ver `modalEscapeStack`).
 * RN Web intenta lo mismo pero en `keyup` y sólo si el modal quedó "activo"
 * tras el `animationend` de apertura — si ese evento no llega (animación
 * interrumpida, dispatch sintético, timing), Escape no hace nada. Acá el modal
 * se apila apenas `visible` es true, sin depender de animaciones.
 *
 * Semántica:
 * - Un modal sin `onRequestClose` (guardando, bloqueado) consume el Escape sin
 *   cerrar — no baja a modales de abajo (igual que el back de Android).
 * - `onRequestClose` envuelto dedup·a el mismo press: nuestro `keydown` cierra
 *   y el listener `keyup` interno de RNW podría llamar `onRequestClose` de
 *   nuevo antes de que el modal se desmonte.
 * - En nativo es transparente: no toca nada, `onRequestClose` llega directo.
 */

const ESCAPE_DEDUPE_MS = 450;

let listenersAttached = false;

function onDocumentKeyDown(event: KeyboardEvent): void {
  if (event.key !== 'Escape' || event.repeat || event.defaultPrevented) return;
  if (!dispatchModalEscape()) return;
  // El modal tope ya consumió la tecla — no dejar que handlers globales la vean.
  event.stopPropagation();
}

function ensureWebEscapeListener(): void {
  if (listenersAttached) return;
  listenersAttached = true;
  document.addEventListener('keydown', onDocumentKeyDown);
}

/** Cuando no queda ningún modal visible el listener sobra. */
function releaseWebEscapeListenerIfIdle(): void {
  if (!listenersAttached || modalEscapeStackSize() > 0) return;
  listenersAttached = false;
  document.removeEventListener('keydown', onDocumentKeyDown);
}

export const Modal = React.forwardRef<React.ElementRef<typeof RNModal>, ModalProps>(function Modal(
  { visible = true, onRequestClose, ...rest },
  ref,
) {
  const onRequestCloseRef = useRef(onRequestClose);
  onRequestCloseRef.current = onRequestClose;

  // En la práctica los callers pasan `() => void`; el evento sintético no se usa.
  const invokeRequestClose = useCallback(() => {
    (onRequestCloseRef.current as (() => void) | undefined)?.();
  }, []);

  const handleRequestClose = useCallback(() => {
    // Dedup del mismo press físico: Escape cierra en keydown (nuestro handler) y
    // RNW puede volver a disparar onRequestClose en el keyup que sigue.
    if (msSinceLastEscapeDispatch() < ESCAPE_DEDUPE_MS) return;
    invokeRequestClose();
  }, [invokeRequestClose]);

  useEffect(() => {
    if (Platform.OS !== 'web' || !visible || typeof document === 'undefined') return;
    ensureWebEscapeListener();
    // Todo modal visible se apila — también sin onRequestClose: tapa a los de abajo.
    const id = pushModalEscape(invokeRequestClose);
    return () => {
      removeModalEscape(id);
      releaseWebEscapeListenerIfIdle();
    };
  }, [visible, invokeRequestClose]);

  return <RNModal ref={ref} visible={visible} onRequestClose={handleRequestClose} {...rest} />;
});
